import { getElementDisplay } from '@components/dynamic-timetable/editor/control-panel/cellDisplay.util';
import { iconCellValueOptions } from '@components/dynamic-timetable/editor/icon/iconCellValueOptions';
import { arrayMove } from '@dnd-kit/sortable';
import {
  findArrayItemById,
  isArrayEmpty,
  isArrayNotEmpty,
  sortArrayAsc,
} from '@hclinear/hc-linear-react';
import { AnchorXDTO } from '@model/dynamicTimeTable/AnchorXDTO';
import { AnchorYDTO } from '@model/dynamicTimeTable/AnchorYDTO';
import { BriefDynamicTimetableDTO } from '@model/dynamicTimeTable/BriefDynamicTimetableDTO';
import { ColorDTO } from '@model/dynamicTimeTable/ColorDTO';
import { DynamicTimetableCellDTO } from '@model/dynamicTimeTable/DynamicTimetableCellDTO';
import { DynamicTimetableCellTypeDTO } from '@model/dynamicTimeTable/DynamicTimetableCellTypeDTO';
import { DynamicTimetableRowDTO } from '@model/dynamicTimeTable/DynamicTimetableRowDTO';
import { ImageResizeType } from '@model/element/ImageResizeType';
import { createSlice, original, PayloadAction } from '@reduxjs/toolkit';
import {
  DynamicTimeTableCanvasHistoryItem,
  DynamicTimeTableCanvasHistoryItemType,
} from '@type/dynamicTimeTable/DynamicTimeTableCanvasHistoryItem';
import { Box } from '@type/layoutEditor/Box';
import {
  areBoxesEqual,
  boxMapper,
  calculateBoxDelta,
  calculateCenterBox,
  floorBoxCoordinates,
  isBoxDimensionsNull,
  isBoxOverflows,
  nullBox,
  transformBoxWithDelta,
} from '@util/box.util';
import { arrayMaxBy, insertAt } from '@/util';
import { Diff, isEmptyObject, objectDifference } from '@/util/object.util';

export interface DynamicTimeTableEditorState {
  isDynamicTTObjectSelected: boolean;
  selectedRowId: number | null;
  selectedCellId: number | null;

  dynamicTimeTable: BriefDynamicTimetableDTO | null;
  rows: DynamicTimetableRowDTO[];

  hiddenRows: Set<number>;
  hiddenCells: Set<number>;

  history: DynamicTimeTableCanvasHistoryItem[];

  canvasUpdatedAt: string | null;
  formUpdatedAt: string | null;
}

const initialDynamicTimeTableEditorState: DynamicTimeTableEditorState = {
  isDynamicTTObjectSelected: false,
  selectedRowId: null,
  selectedCellId: null,

  dynamicTimeTable: null,
  rows: [],

  hiddenRows: new Set(),
  hiddenCells: new Set(),

  history: [],

  canvasUpdatedAt: null,
  formUpdatedAt: null,
};

const DynamicTimeTableEditorSlice = createSlice({
  name: 'DynamicTimeTableEditor',
  initialState: initialDynamicTimeTableEditorState,
  reducers: {
    resetToInitialState(state) {
      Object.assign(state, initialDynamicTimeTableEditorState);
    },
    loadBriefData(state, action: PayloadAction<BriefDynamicTimetableDTO>) {
      state.dynamicTimeTable = {
        ...action.payload,
        x: action.payload.x || 0,
        y: action.payload.y || 0,
      };
    },
    setBriefProperty(state, action: PayloadAction<Partial<BriefDynamicTimetableDTO>>) {
      pushToStateHistory(
        state,
        historizedTimeTableActions.setBriefProperty(state, action.payload),
      );
    },
    setBriefPosition(state, action: PayloadAction<Partial<BriefDynamicTimetableDTO>>) {
      pushToStateHistory(
        state,
        historizedTimeTableActions.setBriefPosition(state, action.payload),
      );
    },
    setDynamicRows(state, action: PayloadAction<DynamicTimetableRowDTO[]>) {
      state.rows = action.payload;

      state.history = [];

      deselect(state);

      orderRowsByYCoordinate(state);
      state.rows.forEach((row) => {
        row.dynamicCells = sortArrayAsc(row.dynamicCells, 'cellOrder');
      });
    },
    deselect(state) {
      deselect(state);
    },
    selectDynamicTTObject(state) {
      selectDynamicTTObject(state);
    },
    selectRow: (state, action: PayloadAction<number>) => {
      selectRow(state, action.payload);
    },
    selectCell: (state, action: PayloadAction<number>) => {
      selectCell(state, action.payload);
    },
    addPreloadedRow(
      state,
      action: PayloadAction<{
        width: number;
        height: number;
        ttFontName: string;
        rasterFontId: number;
      }>,
    ) {
      pushToStateHistory(
        state,
        historizedTimeTableActions.addPreloadedRow(state, action.payload),
      );
    },
    setRowPosition: (state, action: PayloadAction<{ id: number; newPosition: Box }>) => {
      pushToStateHistory(
        state,
        historizedTimeTableActions.setRowPosition(state, action.payload),
      );
    },
    setRowProperty(state, action: PayloadAction<DynamicTimetableRowDTO>) {
      pushToStateHistory(
        state,
        historizedTimeTableActions.setRowProperty(state, {
          id: action.payload.id,
          rowChanges: action.payload,
        }),
      );
    },
    deleteRow(state, action: PayloadAction<number>) {
      pushToStateHistory(
        state,
        historizedTimeTableActions.deleteRow(state, {
          id: action.payload,
        }),
      );
    },
    copyRow(state, action: PayloadAction<number>) {
      pushToStateHistory(
        state,
        historizedTimeTableActions.copyRow(state, {
          id: action.payload,
        }),
      );
    },
    setRowVisibility(state, action: PayloadAction<{ id: number; visible: boolean }>) {
      pushToStateHistory(
        state,
        historizedTimeTableActions.setRowVisibility(state, action.payload),
      );
    },
    addCellToRow(
      state,
      action: PayloadAction<{
        parentRowId: number;
        cellType: DynamicTimetableCellTypeDTO;
        ttFontName: string;
        rasterFontId: number;
      }>,
    ) {
      pushToStateHistory(
        state,
        historizedTimeTableActions.addCellToRow(state, {
          cellType: action.payload.cellType,
          parentRowId: action.payload.parentRowId,
          ttFontName: action.payload.ttFontName,
          rasterFontId: action.payload.rasterFontId,
        }),
      );
    },
    setCellPosition: (state, action: PayloadAction<{ id: number; newPosition: Box }>) => {
      pushToStateHistory(
        state,
        historizedTimeTableActions.setCellPosition(state, action.payload),
      );
    },
    setCellProperty(
      state,
      action: PayloadAction<
        Partial<DynamicTimetableCellDTO> & Pick<DynamicTimetableCellDTO, 'id'>
      >,
    ) {
      pushToStateHistory(
        state,
        historizedTimeTableActions.setCellProperty(state, {
          id: action.payload.id,
          cellChanges: action.payload,
        }),
      );
    },
    deleteCell(state, action: PayloadAction<{ rowId: number; cellId: number }>) {
      pushToStateHistory(
        state,
        historizedTimeTableActions.deleteCell(state, {
          parentRowId: action.payload.rowId,
          cellId: action.payload.cellId,
        }),
      );
    },
    changeCellOrder(
      state,
      action: PayloadAction<{
        parentRowId: number;
        oldIndex: number;
        newIndex: number;
      }>,
    ) {
      pushToStateHistory(
        state,
        historizedTimeTableActions.changeCellOrder(state, action.payload),
      );
    },
    setCellVisibility(state, action: PayloadAction<{ id: number; visible: boolean }>) {
      pushToStateHistory(
        state,
        historizedTimeTableActions.setCellVisibility(state, action.payload),
      );
    },
    undoLastChange(state) {
      if (isArrayEmpty(state.history)) return;
      const historyItem = state.history.pop();
      undoHistoryItem(state, historyItem);
    },
    undoAllChanges(state) {
      while (isArrayNotEmpty(state.history)) {
        const historyItem = state.history.pop();
        undoHistoryItem(state, historyItem);
      }
    },
  },
});

const deselect = (state: DynamicTimeTableEditorState) => {
  state.isDynamicTTObjectSelected = false;
  state.selectedRowId = null;
  state.selectedCellId = null;
};

const selectCell = (state: DynamicTimeTableEditorState, id: number) => {
  state.selectedCellId = id;
  state.isDynamicTTObjectSelected = false;
  state.selectedRowId = null;
};

const selectRow = (state: DynamicTimeTableEditorState, id: number) => {
  state.selectedRowId = id;
  state.isDynamicTTObjectSelected = false;
  state.selectedCellId = null;
};

const selectDynamicTTObject = (state: DynamicTimeTableEditorState) => {
  state.isDynamicTTObjectSelected = true;
  state.selectedRowId = null;
  state.selectedCellId = null;
};

const pushToStateHistory = (
  state: DynamicTimeTableEditorState,
  historyItem: DynamicTimeTableCanvasHistoryItem,
) => {
  if (historyItem === null) return;
  state.history.push(historyItem);
};

const undoHistoryItem = (
  state: DynamicTimeTableEditorState,
  historyItem: DynamicTimeTableCanvasHistoryItem,
) => {
  switch (historyItem.type) {
    case DynamicTimeTableCanvasHistoryItemType.BRIEF_CHANGE: {
      const { diff } = historyItem;
      Object.assign(state.dynamicTimeTable, diff);
      notifyCanvasUpdate(state);
      notifyFormUpdate(state);
      break;
    }
    case DynamicTimeTableCanvasHistoryItemType.ROW_ADD: {
      const row = findArrayItemById(state.rows, historyItem.id);
      const childrenCells = row.dynamicCells;
      const isChildSelected = childrenCells.some((cc) => cc.id === state.selectedCellId);

      if (state.selectedRowId === row.id || isChildSelected) {
        deselect(state);
      }

      historizedTimeTableActions.deleteRow(state, {
        id: historyItem.id,
      });
      break;
    }
    case DynamicTimeTableCanvasHistoryItemType.ROW_DELETE: {
      const deletedRow = historyItem.row;
      state.rows = insertAt(state.rows, historyItem.index, deletedRow);
      orderRowsByYCoordinate(state);
      break;
    }
    case DynamicTimeTableCanvasHistoryItemType.ROW_CHANGE: {
      historizedTimeTableActions.setRowProperty(state, {
        id: historyItem.id,
        rowChanges: historyItem.diff,
      });
      notifyCanvasUpdate(state);
      break;
    }
    case DynamicTimeTableCanvasHistoryItemType.ROW_VISIBILITY_CHANGE: {
      historizedTimeTableActions.setRowVisibility(state, {
        id: historyItem.id,
        visible: historyItem.visible,
      });
      break;
    }

    case DynamicTimeTableCanvasHistoryItemType.CELL_ADD: {
      const isCurrentCellSelected = state.selectedCellId === historyItem.id;
      if (isCurrentCellSelected) {
        deselect(state);
      }
      historizedTimeTableActions.deleteCell(state, {
        parentRowId: historyItem.parentRowId,
        cellId: historyItem.id,
      });
      break;
    }
    case DynamicTimeTableCanvasHistoryItemType.CELL_DELETE: {
      const deletedCell = historyItem.cell;
      const parentRow = state.rows.find((row) => row.id === deletedCell.dynamicTimeRowId);
      parentRow.dynamicCells = insertAt(
        parentRow.dynamicCells,
        historyItem.index,
        deletedCell,
      );
      assignCellOrder(parentRow);
      break;
    }
    case DynamicTimeTableCanvasHistoryItemType.CELL_CHANGE: {
      historizedTimeTableActions.setCellProperty(state, {
        id: historyItem.id,
        cellChanges: historyItem.diff,
      });
      notifyCanvasUpdate(state);
      break;
    }
    case DynamicTimeTableCanvasHistoryItemType.CELL_ORDER_CHANGE: {
      const { parentRowId, oldIndex, newIndex } = historyItem;
      historizedTimeTableActions.changeCellOrder(state, {
        parentRowId,
        oldIndex: newIndex,
        newIndex: oldIndex,
      });
      break;
    }
    case DynamicTimeTableCanvasHistoryItemType.CELL_VISIBILITY_CHANGE: {
      historizedTimeTableActions.setCellVisibility(state, {
        id: historyItem.id,
        visible: historyItem.visible,
      });
      break;
    }
    default: {
      break;
    }
  }
};

const getTimeStamp = () => new Date().toISOString();

const notifyCanvasUpdate = (state: DynamicTimeTableEditorState) => {
  state.canvasUpdatedAt = getTimeStamp();
};

const notifyFormUpdate = (state: DynamicTimeTableEditorState) => {
  state.formUpdatedAt = getTimeStamp();
};

const orderRowsByYCoordinate = (state: DynamicTimeTableEditorState) => {
  state.rows = sortArrayAsc(state.rows, 'y');
  state.rows = state.rows.map((row, index) => ({
    ...row,
    rowOrder: index + 1,
  }));
};

const assignCellOrder = (row: DynamicTimetableRowDTO) => {
  row.dynamicCells = row.dynamicCells.map((cell, index) => ({
    ...cell,
    cellOrder: index + 1,
  }));
};

const historizedTimeTableActions = {
  setBriefProperty(
    state: DynamicTimeTableEditorState,
    data: Partial<BriefDynamicTimetableDTO>,
  ) {
    const oldBrief = { ...original(state.dynamicTimeTable) };
    delete oldBrief.groupIds;
    Object.assign(state.dynamicTimeTable, data);

    const diff = objectDifference(state.dynamicTimeTable, oldBrief);
    if (isEmptyObject(diff)) return null;

    notifyFormUpdate(state);

    return {
      type: DynamicTimeTableCanvasHistoryItemType.BRIEF_CHANGE,
      diff,
    };
  },
  setBriefPosition(
    state: DynamicTimeTableEditorState,
    data: Partial<BriefDynamicTimetableDTO>,
  ) {
    const oldBrief = { ...state.dynamicTimeTable };
    Object.assign(state.dynamicTimeTable, data);

    const hasBoxChanged = !areBoxesEqual(oldBrief, state.dynamicTimeTable);

    if (!hasBoxChanged) return null;

    const diff = objectDifference(state.dynamicTimeTable, oldBrief);

    notifyCanvasUpdate(state);

    return {
      type: DynamicTimeTableCanvasHistoryItemType.BRIEF_CHANGE,
      diff,
    };
  },
  addPreloadedRow(
    state: DynamicTimeTableEditorState,
    data: {
      width: number;
      height: number;
      ttFontName: string;
      rasterFontId: number;
    },
  ) {
    const { width, height, ttFontName, rasterFontId } = data;
    const newRow = {
      id: getNextTempId(),
      width,
      height,
      x: 0,
      y: 0,
      rowOrder: 0,
      eraserColor: ColorDTO.FFF_White,
      dynamicCells: [],
    };
    state.rows.push(newRow);
    selectRow(state, newRow.id);

    Object.keys(DynamicTimetableCellTypeDTO).forEach((cellType) => {
      historizedTimeTableActions.addCellToRow(state, {
        cellType: cellType as DynamicTimetableCellTypeDTO,
        parentRowId: newRow.id,
        ttFontName,
        rasterFontId,
      });
    });

    orderRowsByYCoordinate(state);
    return {
      type: DynamicTimeTableCanvasHistoryItemType.ROW_ADD,
      id: newRow.id,
    };
  },
  setRowPosition: (
    state: DynamicTimeTableEditorState,
    data: { id: number; newPosition: Box },
  ) => {
    const { id, newPosition } = data;

    const flooredNewPosition = floorBoxCoordinates(newPosition);

    const row = findArrayItemById(state.rows, id);
    if (!row) throw new Error(`Row with id ${id} not found!`);
    const oldPosition: Box = boxMapper(row);
    const boxDelta = calculateBoxDelta(oldPosition, flooredNewPosition);
    const hasBoxChanged = !areBoxesEqual(oldPosition, flooredNewPosition);
    if (!hasBoxChanged) {
      console.log(`Box with id ${id} position did not change!`);
      return null;
    }
    Object.assign(row, flooredNewPosition);
    let affectsChildren = false;
    const hasOnlyRowPositionChanged = isBoxDimensionsNull(boxDelta);

    if (hasOnlyRowPositionChanged) {
      affectsChildren = true;
      row.dynamicCells.forEach((dynamicCell) => {
        Object.assign(dynamicCell, transformBoxWithDelta(dynamicCell, boxDelta));
      });
    }

    orderRowsByYCoordinate(state);
    notifyCanvasUpdate(state);
    return {
      type: DynamicTimeTableCanvasHistoryItemType.ROW_CHANGE,
      id: row.id,
      diff: oldPosition,
      affectsChildren,
    };
  },
  setRowProperty(
    state: DynamicTimeTableEditorState,
    data: { id: number; rowChanges: Partial<DynamicTimetableRowDTO> },
  ) {
    const { id, rowChanges } = data;

    const row = findArrayItemById(state.rows, id);
    if (!row) {
      console.error(`Row with id ${id} not found!`);
      return null;
    }

    const prevBox: Box = boxMapper(row);
    const oldRow = { ...row };
    delete (oldRow as any).dynamicCells;

    const { dynamicCells: _ignoredDynamicCells, ...safeChanges } = rowChanges as any;

    Object.assign(row, safeChanges);

    const nextBox: Box = boxMapper(row);
    const newRow = { ...row };
    delete (newRow as any).dynamicCells;

    const diff: Diff<DynamicTimetableRowDTO> = objectDifference(newRow, oldRow);
    if (isArrayEmpty(Object.keys(diff))) {
      console.log(`Row with id ${id} did not change!`);
      return null;
    }

    const boxDelta = calculateBoxDelta(prevBox, nextBox);
    const hasOnlyRowPositionChanged = isBoxDimensionsNull(boxDelta);

    let affectsChildren = false;

    if (hasOnlyRowPositionChanged && (boxDelta.x !== 0 || boxDelta.y !== 0)) {
      affectsChildren = true;

      row.dynamicCells.forEach((dynamicCell) => {
        const movedBox = transformBoxWithDelta(boxMapper(dynamicCell), {
          x: boxDelta.x,
          y: boxDelta.y,
        });
        Object.assign(dynamicCell, movedBox);
      });
      orderRowsByYCoordinate(state);
    }

    notifyFormUpdate(state);

    return {
      type: DynamicTimeTableCanvasHistoryItemType.ROW_CHANGE,
      id: row.id,
      diff: prevBox,
      affectsChildren,
    };
  },
  deleteRow(state: DynamicTimeTableEditorState, data: { id: number }) {
    const { id } = data;
    const row = findArrayItemById(state.rows, id);
    if (!row) throw new Error(`Row with id ${id} not found!`);

    const rowIndex = state.rows.indexOf(row);
    state.rows = state.rows.filter((row) => row.id !== id);
    orderRowsByYCoordinate(state);
    return {
      type: DynamicTimeTableCanvasHistoryItemType.ROW_DELETE,
      row,
      index: rowIndex,
    };
  },
  copyRow(state: DynamicTimeTableEditorState, data: { id: number }) {
    const { id } = data;
    const row = findArrayItemById(state.rows, id);
    if (!row) throw new Error(`Row with id ${id} not found!`);
    const newRow = structuredClone(original(row));

    const rowBoxDelta = calculateBoxDelta(row, nullBox);

    const transFormDelta: Box = {
      ...rowBoxDelta,
      width: 0,
      height: 0,
    };

    newRow.id = getNextTempId();

    newRow.dynamicCells.forEach((dynamicCell) => {
      const propertyCorrections: Partial<DynamicTimetableCellDTO> = {
        ...transformBoxWithDelta(boxMapper(dynamicCell), transFormDelta),
        dynamicTimeRowId: newRow.id,
        id: getNextTempId(),
      };

      Object.assign(dynamicCell, propertyCorrections);
    });

    Object.assign(newRow, transformBoxWithDelta(boxMapper(newRow), transFormDelta));

    state.rows.push(newRow);
    selectRow(state, newRow.id);
    orderRowsByYCoordinate(state);

    return {
      type: DynamicTimeTableCanvasHistoryItemType.ROW_ADD,
      id: newRow.id,
    };
  },
  setRowVisibility(
    state: DynamicTimeTableEditorState,
    data: { id: number; visible: boolean },
  ) {
    const { id, visible } = data;
    if (visible) {
      state.hiddenRows.delete(id);
    } else {
      state.hiddenRows.add(id);
    }
    return {
      type: DynamicTimeTableCanvasHistoryItemType.ROW_VISIBILITY_CHANGE,
      id,
      visible: !visible,
    };
  },
  addCellToRow(
    state: DynamicTimeTableEditorState,
    data: {
      cellType: DynamicTimetableCellTypeDTO;
      parentRowId: number;
      ttFontName: string;
      rasterFontId: number;
    },
  ) {
    const { cellType, parentRowId, rasterFontId, ttFontName } = data;
    const parentRow = findArrayItemById(state.rows, parentRowId);
    if (!parentRow) throw new Error(`Row with id ${parentRowId} not found!`);

    const elementWidth = 100;
    const elementHeight = 100;
    const gap = 50;

    const basePosition: Box = {
      x: parentRow.x,
      y: parentRow.y,
      width: elementWidth,
      height: elementHeight,
    };

    const centerPosition: Box = calculateCenterBox(
      parentRow,
      elementWidth,
      elementHeight,
    );

    const farthestRightCell = arrayMaxBy(
      parentRow.dynamicCells,
      (item) => item.x + item.width,
    );

    let newPositionBox: Box = farthestRightCell
      ? {
          x: farthestRightCell.x + farthestRightCell.width + gap,
          y: farthestRightCell.y,
          width: elementWidth,
          height: elementHeight,
        }
      : basePosition;

    const isOverflow = isBoxOverflows(newPositionBox, parentRow);

    if (isOverflow) {
      newPositionBox = centerPosition;
    }

    const textValue = (() => {
      switch (cellType) {
        case DynamicTimetableCellTypeDTO.DEPARTURE_TIME:
          return '23:59';
        case DynamicTimetableCellTypeDTO.SCHEDULED_TIME:
          return '23:58';
        default:
          return iconCellValueOptions[cellType]?.[0] ?? getElementDisplay(cellType);
      }
    })();

    const newCell: DynamicTimetableCellDTO = {
      id: getNextTempId(),
      dynamicTimeRowId: parentRowId,
      x: newPositionBox.x,
      y: newPositionBox.y,
      width: newPositionBox.width,
      height: newPositionBox.height,
      anchorX: AnchorXDTO.LEFT,
      anchorY: AnchorYDTO.TOP,
      backgroundColor: ColorDTO.FFF_White,
      fontColor: ColorDTO._000_Black,
      textValue,
      rasterFontId,
      ttFontName,
      cellOrder: parentRow.dynamicCells.length + 1,
      type: cellType,
      countdownLimitNear: 30,
      exactTimeperiod: 9999,
      delayThreshold: 5,
      imageResizeType:
        cellType === DynamicTimetableCellTypeDTO.IMAGE ? ImageResizeType.FIX : undefined,
    };

    parentRow.dynamicCells.push(newCell);
    assignCellOrder(parentRow);
    return {
      type: DynamicTimeTableCanvasHistoryItemType.CELL_ADD,
      id: newCell.id,
      parentRowId: parentRowId,
    };
  },
  setCellPosition: (
    state: DynamicTimeTableEditorState,
    data: { id: number; newPosition: Box },
  ) => {
    const { id, newPosition } = data;
    const flooredNewPosition = floorBoxCoordinates(newPosition);
    const cells = state.rows.flatMap((row) => row.dynamicCells);
    const cell = findArrayItemById(cells, id);
    if (!cell) throw new Error(`Cell with id ${id} not found!`);
    const oldPosition: Box = boxMapper(cell);
    const hasBoxChanged = !areBoxesEqual(oldPosition, flooredNewPosition);
    if (!hasBoxChanged) {
      console.log(`Box with id ${id} position did not change!`);
      return null;
    }
    Object.assign(cell, flooredNewPosition);
    notifyCanvasUpdate(state);
    return {
      type: DynamicTimeTableCanvasHistoryItemType.CELL_CHANGE,
      id: cell.id,
      diff: oldPosition,
    };
  },
  setCellProperty(
    state: DynamicTimeTableEditorState,
    data: {
      id: number;
      cellChanges: Partial<DynamicTimetableCellDTO>;
    },
  ) {
    const { id, cellChanges } = data;
    const cell = findArrayItemById(
      state.rows.flatMap((row) => row.dynamicCells),
      id,
    );
    if (!cell) {
      console.error(`Cell with id ${id} not found!`);
      return;
    }

    const oldCell = { ...cell };
    Object.assign(cell, cellChanges);

    const diff = objectDifference(cell, oldCell);

    if (isArrayEmpty(Object.keys(diff))) {
      console.log(`Cell with id ${id} did not change!`);
      return null;
    }

    notifyFormUpdate(state);
    return {
      type: DynamicTimeTableCanvasHistoryItemType.CELL_CHANGE,
      id: cell.id,
      diff,
    };
  },
  changeCellOrder(
    state: DynamicTimeTableEditorState,
    data: {
      parentRowId: number;
      oldIndex: number;
      newIndex: number;
    },
  ) {
    const { parentRowId, oldIndex, newIndex } = data;
    const row = findArrayItemById(state.rows, parentRowId);
    if (!row) throw new Error(`Row with id ${parentRowId} not found!`);
    row.dynamicCells = arrayMove(row.dynamicCells, oldIndex, newIndex).map(
      (cell, index) => ({
        ...cell,
        cellOrder: index + 1,
      }),
    );
    return {
      type: DynamicTimeTableCanvasHistoryItemType.CELL_ORDER_CHANGE,
      parentRowId: row.id,
      oldIndex,
      newIndex: newIndex,
    };
  },
  deleteCell(
    state: DynamicTimeTableEditorState,
    data: { parentRowId: number; cellId: number },
  ) {
    const { parentRowId, cellId } = data;
    const row = findArrayItemById(state.rows, parentRowId);
    if (!row) throw new Error(`Row with id ${parentRowId} not found!`);

    const cell = findArrayItemById(row.dynamicCells, cellId);
    if (!cell) throw new Error(`Cell with id ${cellId} not found!`);

    const cellIndex = row.dynamicCells.indexOf(cell);
    row.dynamicCells = row.dynamicCells.filter(
      (dynamicCell) => dynamicCell.id !== cellId,
    );

    assignCellOrder(row);
    return {
      type: DynamicTimeTableCanvasHistoryItemType.CELL_DELETE,
      cell,
      index: cellIndex,
    };
  },
  setCellVisibility(
    state: DynamicTimeTableEditorState,
    data: { id: number; visible: boolean },
  ) {
    const { id, visible } = data;

    if (visible) {
      state.hiddenCells.delete(id);
    } else {
      state.hiddenCells.add(id);
    }
    return {
      type: DynamicTimeTableCanvasHistoryItemType.CELL_VISIBILITY_CHANGE,
      id,
      visible: !visible,
    };
  },
} satisfies {
  [key: string]: (...params: unknown[]) => DynamicTimeTableCanvasHistoryItem;
};

let TEMP_ID_SEQUENCE = Number.MIN_SAFE_INTEGER;

function getNextTempId() {
  return TEMP_ID_SEQUENCE++;
}

export default DynamicTimeTableEditorSlice.reducer;
export const dynamicTimeTableEditorActions = DynamicTimeTableEditorSlice.actions;
